import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useHelpCenterQuery } from 'hooks/useHelpCenter';
import { API } from 'utils/Helper';

// Styled components
const HelpCenterContainer = styled.div`
  display: flex;
  color: #ffffff;
  gap: 10px;
  align-items: flex-start;
  position: relative;

  @media(max-width:1100px){
    flex-direction: column;
  }
`;

const SideContent = styled.div`
  width: 300px;
  padding: 10px;
  background-color: rgb(50, 55, 56);
  border-radius: 10px;  
  align-self: flex-start;

  @media (max-width: 1100px) {
    width: 100%;
    display: flex;
    overflow-x: scroll;  
    padding: 5px;
    gap: 10px;
  }
`;

const SideContentItem = styled.div`
  padding: 10px 0 10px 20px;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
  display: flex;
  gap: 10px;
  align-items: center;
  color: ${props => (props.active ? 'white' : '#B3B3B3')};
  background-color: ${props => (props.active ? '#3A4142' : 'transparent')};
  border-radius: ${props => (props.active ? '10px' : '0')};

  img {
    width: 18px;
    height: 18px;
    object-contain: fit;
  }

  @media (max-width: 1100px) {
    width: auto;  
    max-width: 200px;
    flex-shrink: 0; 
    padding: 10px;
  }

  @media (max-width: 545px) {
    font-size: 12px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-height: 100%;
`;

const ContentSection = styled.div`
  background-color: rgb(50 55 56);
  padding: 24px;
  border-radius: 15px;
  color: #b9bbbe;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 20px;

  h2, h3, h4 {
    color: #ffffff;
    margin-top: 20px;
    margin-bottom: 12px;
    font-weight: bold;
  }
  h2 { font-size: 24px; border-bottom: 1px solid #3A4142; padding-bottom: 8px; }
  h3 { font-size: 18px; }
  h4 { font-size: 16px; }
  p, li { margin-bottom: 12px; }
  ol, ul { padding-left: 20px; margin-bottom: 15px; }
  li { list-style-type: decimal; }
  ul li { list-style-type: disc; }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
    margin-bottom: 15px;
  }
  th {
    color: #b9bbbe;
    font-weight: 600;
    text-align: left;
    padding: 12px;
    background-color: #3A4142;
  }
  td {
    color: #ffffff;
    padding: 12px;
    border-bottom: 1px solid #3A4142;
  }
  tr:nth-child(even) {
    background-color: #2b2e35;
  }
`;

const ProvablyFairSelect = styled.div`
  display: none;
  border-radius: 1.25rem;
  padding: 0.125rem 0.375rem;
  background-color: rgb(23, 24, 27);
  box-shadow: rgba(0, 0, 0, 0.14) 0px 0px 8px 0px;
  height: auto;
  max-height: 16.25rem;
  box-sizing: border-box;
  overflow-y: auto;
  z-index: 9999;
  touch-action: pan-y;
  overscroll-behavior: contain;
`;

const List = styled.div`
  margin-top: 0;
  color: ${props => props.active ? '#ffffff' : 'rgba(245, 246, 247, 0.6)'};
  font-weight: bold;
  height: 2.25rem;
  border: ${props => props.active ? '1px solid rgba(91, 174, 28, 1)' : ''};
  font-size: 0.875rem;
  display: flex;
  align-items: center; 
  position: relative;
  padding: 0 0.625rem;
  margin: 0.25rem 0;
  border-radius: 1.0625rem;
  cursor: pointer;
  white-space: nowrap; 
  background-color: transparent; 

  &:hover {
    border-color: rgba(91, 174, 28, 1);  
  }
`;

const ResponsiveWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

// Skeleton Styled Components
const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

const SkeletonWrapper = styled.div`
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
`;

const SkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${pulse} 1.5s ease-in-out infinite;
  
  .icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-color: #323738;
  }
  
  .title {
    width: 120px;
    height: 20px;
    border-radius: 4px;
    background-color: #323738;
  }
`;

const SkeletonLayout = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;

  @media(max-width:1100px){
    flex-direction: column;
  }
`;

const SkeletonSidebar = styled.div`
  width: 300px;
  padding: 15px;
  background-color: rgb(50, 55, 56);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    width: 100%;
    flex-direction: row;
    overflow-x: hidden;
  }
`;

const SkeletonSidebarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${pulse} 1.5s ease-in-out infinite;

  .icon {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background-color: #3a4142;
  }

  .text {
    width: 140px;
    height: 15px;
    border-radius: 4px;
    background-color: #3a4142;
  }

  @media (max-width: 1100px) {
    .text {
      width: 80px;
    }
  }
`;

const SkeletonMain = styled.div`
  flex: 1;
  width: 100%;
  background-color: rgb(50, 55, 56);
  padding: 24px;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const SkeletonLine = styled.div`
  height: ${props => props.height || '14px'};
  width: ${props => props.width || '100%'};
  border-radius: 4px;
  background-color: #3a4142;
`;

const ProvablyFair = () => {
    const history = useHistory();
    const location = useLocation();
    const [isSelectOpen, setSelectOpen] = useState(false);  
    const { data: helpPages = [], isLoading: loading } = useHelpCenterQuery();

    const queryParams = new URLSearchParams(location.search);
    const activeTabSlug = queryParams.get('tab');

    // Find active section
    let currentActiveItem = null;
    if (helpPages.length > 0) {
        currentActiveItem = helpPages.find(item => item.slug === activeTabSlug) || helpPages[0];
    }

    const handleListItemClick = (slug) => {
        history.push({
            pathname: location.pathname,
            search: `?tab=${slug}`
        });
        setSelectOpen(false); 
    };

    if (loading) {
        return (
            <SkeletonWrapper>
                <SkeletonHeader>
                    <div className="icon" />
                    <div className="title" />
                </SkeletonHeader>
                <SkeletonLayout>
                    <SkeletonSidebar>
                        {[1, 2, 3, 4, 5].map(i => (
                            <SkeletonSidebarItem key={i}>
                                <div className="icon" />
                                <div className="text" />
                            </SkeletonSidebarItem>
                        ))}
                    </SkeletonSidebar>
                    <SkeletonMain>
                        <SkeletonLine height="24px" width="40%" />
                        <div style={{ borderBottom: '1px solid #3a4142', margin: '4px 0' }} />
                        <SkeletonLine height="14px" width="100%" />
                        <SkeletonLine height="14px" width="95%" />
                        <SkeletonLine height="14px" width="90%" />
                        <SkeletonLine height="14px" width="85%" />
                        <div style={{ margin: '8px 0' }} />
                        <SkeletonLine height="20px" width="25%" />
                        <SkeletonLine height="14px" width="100%" />
                        <SkeletonLine height="14px" width="92%" />
                        <SkeletonLine height="14px" width="70%" />
                    </SkeletonMain>
                </SkeletonLayout>
            </SkeletonWrapper>
        );
    }

    if (helpPages.length === 0) {
        return (
            <div style={{ marginTop: '80px', textAlign: 'center', color: '#B3B3B3', fontWeight: 'bold' }}>
                No Help Center sections configured.
            </div>
        );
    }

    return (
        <div style={{ marginTop: '25px'}}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0', color: '', display: 'flex', gap: '10px', alignContent: 'center', alignItems: 'center' }}>
                <img style={{height:'20px', width:'20px'}} src='/assets/images/taraju.png' />
                <div>Help Center</div>
            </div>

            <HelpCenterContainer style={{marginTop: '30px'}}>
                <SideContent>
                    {helpPages.map(item => (
                        <SideContentItem
                            key={item.slug}
                            active={currentActiveItem?.slug === item.slug}
                            onClick={() => {
                                history.push({
                                    pathname: location.pathname,
                                    search: `?tab=${item.slug}`
                                });
                            }}
                        >
                            {item.icon_url && <img src={item.icon_url} alt={item.title} />}
                            {item.title}
                        </SideContentItem>
                    ))}
                </SideContent>

                <ResponsiveWrapper>
                    {isSelectOpen && (  
                        <ProvablyFairSelect>
                            {helpPages.map(item => (
                                <List 
                                    key={item.slug}
                                    active={currentActiveItem?.slug === item.slug}
                                    onClick={() => handleListItemClick(item.slug)}
                                >
                                    {item.title}
                                </List>
                            ))}
                        </ProvablyFairSelect>
                    )}

                    <MainContent>
                        {currentActiveItem && (
                            <ContentSection dangerouslySetInnerHTML={{ __html: currentActiveItem.content }} />
                        )}
                    </MainContent>
                </ResponsiveWrapper>
                
            </HelpCenterContainer>

        </div>
    );
};

export default ProvablyFair;
